/**
 * Email Templates — every email the portal sends, previewed view-only.
 *
 * Two tabs: notification emails (sample content in the viewer's branding;
 * Helix staff see platform branding) and scheduled report emails (the
 * viewer's company's Reports schedules, rendered exactly as sent, charts
 * inline). Embedded in Admin Setup > Communications > Email Templates, and
 * still reachable on its own at /admin/email-templates.
 *
 * The API decides who may see what (customer admins and Helix staff only;
 * a customer admin sees only their own company's schedules) — this screen
 * only mirrors that gate.
 */
import { useEffect, useState } from 'react';
import type { EmailTemplateType, ScheduledReportEmail } from '../../lib/api';
import {
  canViewEmailTemplates,
  loadNotificationPreview,
  loadScheduledReportPreview,
  useEmailPreview,
  useNotificationTemplateTypes,
  useScheduledReportEmails,
} from '../../components/admin/settings/emailTemplates/useEmailTemplates';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import EmailPreviewFrame from '../../components/admin/settings/emailTemplates/EmailPreviewFrame';
import NotificationTemplateList from '../../components/admin/settings/emailTemplates/NotificationTemplateList';
import ScheduledReportEmailList from '../../components/admin/settings/emailTemplates/ScheduledReportEmailList';
import { Bell, CalendarClock, Eye } from 'lucide-react';

interface EmailTemplatePreviewProps {
  /** Inside Admin Setup: drop the page heading, the section supplies its own. */
  embedded?: boolean;
}

type EmailTab = 'notifications' | 'reports';

export default function EmailTemplatePreview({ embedded = false }: EmailTemplatePreviewProps) {
  const { user } = useAuth();
  // Checked before the body mounts, so a blocked user fetches nothing.
  if (!canViewEmailTemplates(user)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You do not have access to Email Templates.</p>
      </div>
    );
  }
  return <EmailTemplatesBody embedded={embedded} showCompany={user?.company_type === 'helix'} />;
}

interface EmailTemplatesBodyProps {
  embedded: boolean;
  /** Helix staff see every company's schedules, so each row names its company. */
  showCompany: boolean;
}

function EmailTemplatesBody({ embedded, showCompany }: EmailTemplatesBodyProps) {
  const [tab, setTab] = useState<EmailTab>('notifications');
  const [selectedType, setSelectedType] = useState<EmailTemplateType | null>(null);
  const [selectedReport, setSelectedReport] = useState<ScheduledReportEmail | null>(null);

  const types = useNotificationTemplateTypes();
  const reports = useScheduledReportEmails();

  // CR-7725: both lists are tenant-specific, so a row selected for one customer
  // can be absent for the next. The selection is plain state and would survive
  // the switch, leaving the preview pane rendering an email the customer now on
  // screen is not entitled to see — the list would hide it while the right-hand
  // pane still showed it.
  useEffect(() => {
    if (!selectedType || !types.data) return;
    const stillListed = Object.values(types.data).some(
      (rows) => rows.some((row) => row.value === selectedType.value),
    );
    if (!stillListed) setSelectedType(null);
  }, [types.data, selectedType]);

  useEffect(() => {
    if (!selectedReport || !reports.data) return;
    if (!reports.data.some((report) => report.id === selectedReport.id)) {
      setSelectedReport(null);
    }
  }, [reports.data, selectedReport]);

  const typeKey = selectedType && selectedType.has_sample ? `notification:${selectedType.value}` : null;
  const notificationPreview = useEmailPreview(
    typeKey, () => loadNotificationPreview(selectedType?.value ?? ''),
  );
  const reportPreview = useEmailPreview(
    selectedReport ? `report:${selectedReport.id}` : null,
    () => loadScheduledReportPreview(selectedReport?.id ?? 0),
  );

  return (
    <div className="space-y-4">
      {embedded ? (
        <div>
          <h2 className="text-lg font-semibold">Email Templates</h2>
          <p className="text-sm text-muted-foreground">
            Every email the portal sends on your behalf, shown with your branding. View only.
          </p>
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Eye className="h-6 w-6" /> Email Templates
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Preview every notification and scheduled report email, as recipients see it
          </p>
        </div>
      )}

      <Tabs value={tab} onValueChange={(value) => setTab(value as EmailTab)}>
        <TabsList>
          <TabsTrigger value="notifications" className="gap-1.5">
            <Bell className="h-4 w-4" /> Notification emails
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5">
            <CalendarClock className="h-4 w-4" /> Scheduled report emails
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="mt-4">
          <div className="flex gap-6">
            <NotificationTemplateList
              grouped={types.data}
              loading={types.loading}
              error={types.error}
              onRetry={() => void types.refetch()}
              selected={selectedType?.value ?? null}
              onSelect={setSelectedType}
            />
            <EmailPreviewFrame
              title={selectedType?.label ?? null}
              preview={notificationPreview.preview}
              loading={notificationPreview.loading}
              error={notificationPreview.error}
              onRetry={notificationPreview.reload}
              noSample={Boolean(selectedType && !selectedType.has_sample)}
              emptyHint="Choose a notification on the left to see it with your branding."
            />
          </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <div className="flex gap-6">
            <ScheduledReportEmailList
              reports={reports.data}
              loading={reports.loading}
              error={reports.error}
              onRetry={() => void reports.refetch()}
              selectedId={selectedReport?.id ?? null}
              onSelect={setSelectedReport}
              showCompany={showCompany}
            />
            <EmailPreviewFrame
              title={selectedReport ? `${selectedReport.dashboard_name} (${selectedReport.frequency_display})` : null}
              preview={reportPreview.preview}
              loading={reportPreview.loading}
              error={reportPreview.error}
              onRetry={reportPreview.reload}
              emptyHint="Choose a scheduled report to see the email its recipients get, charts included."
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
